export type PtCode = 'JGI' | 'JGT';

const STORAGE_KEY = 'laporanjp.targetPt';

type CompanyRow = { id: number; code: string; name: string };

/** Shared across pages — one session PT + company list. */
const targetCompanyCode = ref<PtCode | null>(null);
/** DBS / Kartu Kredit bersama — tanpa PT (neraca 50:50). */
const sharedMode = ref(false);
const companies = ref<CompanyRow[]>([]);
let companiesLoad: Promise<void> | null = null;
let persistWatchBound = false;

const FALLBACK_COMPANIES: CompanyRow[] = [
  { id: -1, code: 'JGI', name: 'Jasa Gudang Indonesia' },
  { id: -2, code: 'JGT', name: 'Jasa Gudang Tangerang' },
];

function normalizeCompanyCode(raw: unknown): PtCode | null {
  const c = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/^PT\.?\s+/i, '');
  if (c === 'JGI' || c === 'JGT') return c;
  if (/\bJGI\b/.test(c)) return 'JGI';
  if (/\bJGT\b/.test(c)) return 'JGT';
  return null;
}

function ensureJgiJgtPresent(list: CompanyRow[]): CompanyRow[] {
  const out = [...list];
  for (const fb of FALLBACK_COMPANIES) {
    if (!out.some((c) => normalizeCompanyCode(c.code) === fb.code)) {
      out.push({ ...fb });
    }
  }
  return out.map((c, i) => {
    const code = normalizeCompanyCode(c.code) || String(c.code || '').toUpperCase();
    return {
      id: Number(c.id) || -(i + 1),
      code,
      name: String(c.name || code),
    };
  });
}

function persistTarget(persist: boolean) {
  if (!persist || !import.meta.client) return;
  try {
    if (targetCompanyCode.value) sessionStorage.setItem(STORAGE_KEY, targetCompanyCode.value);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function restoreFromStorage(persist: boolean) {
  if (!persist || !import.meta.client) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === 'JGI' || raw === 'JGT') targetCompanyCode.value = raw;
  } catch {
    /* ignore */
  }
}

function bindPersistWatch() {
  if (persistWatchBound) return;
  persistWatchBound = true;
  watch(targetCompanyCode, () => persistTarget(true));
}

/** Shared PT target for upload / import / journals — prevents mixed JGI/JGT data. */
export function usePtTarget(opts?: { persist?: boolean }) {
  const persist = opts?.persist !== false;
  bindPersistWatch();

  const companyItems = computed(() =>
    companies.value
      .filter((c) => normalizeCompanyCode(c.code) === 'JGI' || normalizeCompanyCode(c.code) === 'JGT')
      .map((c) => {
        const code = normalizeCompanyCode(c.code)!;
        return {
          title: `${code} — ${String(c.name || '').replace(/^PT\.?\s+/i, '')}`,
          value: code,
          id: c.id,
          code,
          name: c.name,
        };
      })
  );

  const selectedCompany = computed(
    () =>
      companies.value.find((c) => normalizeCompanyCode(c.code) === targetCompanyCode.value) || null
  );

  const targetCompanyId = computed(() => {
    const id = selectedCompany.value?.id;
    return id != null && id > 0 ? id : null;
  });

  const isSharedMode = computed(() => sharedMode.value);

  const hasPt = computed(
    () =>
      targetCompanyCode.value === 'JGI' ||
      targetCompanyCode.value === 'JGT' ||
      sharedMode.value
  );

  function companyLabel(name: string) {
    return String(name || '')
      .replace(/^PT\.?\s+/i, '')
      .trim();
  }

  function setTarget(code: PtCode | null) {
    targetCompanyCode.value = code === 'JGI' || code === 'JGT' ? code : null;
    if (code) sharedMode.value = false;
  }

  function setSharedMode(on: boolean) {
    sharedMode.value = on;
    if (on) targetCompanyCode.value = null;
  }

  async function loadCompanies(list?: CompanyRow[]) {
    const run = async () => {
      let rows: CompanyRow[] = [];
      try {
        if (list?.length) {
          rows = list;
        } else {
          rows = await $fetch<CompanyRow[]>('/api/companies');
        }
      } catch {
        rows = companies.value.length ? companies.value : [];
      }
      companies.value = ensureJgiJgtPresent(Array.isArray(rows) ? rows : []);
      restoreFromStorage(persist);
      if (
        targetCompanyCode.value &&
        !companies.value.some((c) => normalizeCompanyCode(c.code) === targetCompanyCode.value)
      ) {
        targetCompanyCode.value = null;
      }
    };

    if (list?.length) {
      await run();
      return;
    }
    if (!companiesLoad) {
      companiesLoad = run().finally(() => {
        companiesLoad = null;
      });
    }
    await companiesLoad;
  }

  /** Stamp every row with the session target PT (force = overwrite inferred PT). */
  function stampRows<T extends { companyCode?: PtCode | null }>(
    rows: T[],
    optsStamp?: { force?: boolean }
  ): T[] {
    if (sharedMode.value) return rows;
    const code = targetCompanyCode.value;
    if (!code) return rows;
    const force = optsStamp?.force !== false;
    return rows.map((r) => ({
      ...r,
      companyCode: force || !r.companyCode ? code : r.companyCode,
    }));
  }

  return {
    targetCompanyCode,
    targetCompanyId,
    companies,
    companyItems,
    selectedCompany,
    hasPt,
    isSharedMode,
    sharedMode,
    companyLabel,
    setTarget,
    setSharedMode,
    loadCompanies,
    stampRows,
    restoreFromStorage: () => restoreFromStorage(persist),
  };
}
